import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { contactsApi } from '../api/contacts';
import './ContactDetailPage.css';

const STAGE_CONFIG = {
  new:         { label: 'New',         color: '#6B7280' },
  contacted:   { label: 'Contacted',   color: '#00D9FF' },
  engaged:     { label: 'Engaged',     color: '#A855F7' },
  opportunity: { label: 'Opportunity', color: '#FFB800' },
  partner:     { label: 'Partner',     color: '#39FF14' },
  inactive:    { label: 'Inactive',    color: '#374151' },
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 260, damping: 22 } },
};

export default function ContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [stageSaving, setStageSaving] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const data = await contactsApi.getOne(id);
        setContact(data.contact);
        setNotes(data.contact.notes || '');
      } catch (err) {
        console.error('[ContactDetail]', err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  const handleStageChange = async (newStage) => {
    if (!contact || stageSaving) return;
    setStageSaving(true);
    try {
      const data = await contactsApi.update(id, { stage: newStage });
      setContact(data.contact);
    } catch (err) {
      console.error('[ContactDetail stage]', err.message);
    } finally {
      setStageSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!contact) return;
    setNotesSaving(true);
    try {
      const data = await contactsApi.update(id, { notes });
      setContact(data.contact);
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    } catch (err) {
      console.error('[ContactDetail notes]', err.message);
    } finally {
      setNotesSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="detail-loading">
        <div className="detail-spinner" />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="detail-not-found">
        <h2>Contact not found</h2>
        <button onClick={() => navigate('/contacts')}>Back to Contacts</button>
      </div>
    );
  }

  const stageCfg = STAGE_CONFIG[contact.stage] || STAGE_CONFIG.new;
  const name = contact.company || `${contact.firstName} ${contact.lastName || ''}`.trim();
  const initial = name[0]?.toUpperCase() || '?';

  return (
    <div className="contact-detail">
      <div className="detail-inner">
        <button className="back-btn" onClick={() => navigate('/contacts')}>
          ← Back to Contacts
        </button>

        <motion.div
          className="detail-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header Card */}
          <motion.div className="detail-card detail-card--header" variants={cardVariants}>
            <div
              className="detail-avatar"
              style={{ background: `${stageCfg.color}22`, border: `3px solid ${stageCfg.color}66` }}
            >
              {contact.avatarImage
                ? <img src={contact.avatarImage} alt={name} />
                : <span style={{ color: stageCfg.color }}>{initial}</span>}
            </div>
            <div className="detail-header-info">
              <h1 className="detail-name">{name}</h1>
              <p className="detail-type">
                {contact.contactType?.charAt(0).toUpperCase() + contact.contactType?.slice(1).replace(/([A-Z])/g, ' $1')}
              </p>
              {contact.email && <a href={`mailto:${contact.email}`} className="detail-email">{contact.email}</a>}
            </div>
            {contact.communityId && (
              <a
                href={`https://community.knkt.io/contacts/${contact.communityId}`}
                target="_blank"
                rel="noreferrer"
                className="community-link"
              >
                View in Community ↗
              </a>
            )}
          </motion.div>

          {/* Stage Selector */}
          <motion.div className="detail-card" variants={cardVariants}>
            <h3 className="card-title">Pipeline Stage</h3>
            <div className="stage-selector">
              {Object.entries(STAGE_CONFIG).map(([s, cfg]) => (
                <button
                  key={s}
                  className={`stage-select-btn ${contact.stage === s ? 'active' : ''}`}
                  style={{ '--stage-color': cfg.color }}
                  onClick={() => handleStageChange(s)}
                  disabled={stageSaving}
                >
                  {cfg.label}
                </button>
              ))}
            </div>
            {stageSaving && <p className="saving-hint">Saving...</p>}
          </motion.div>

          {/* Score */}
          <motion.div className="detail-card" variants={cardVariants}>
            <h3 className="card-title">Lead Score</h3>
            <div className="score-display">
              <span
                className="score-big"
                style={{
                  color: contact.score >= 75 ? '#39FF14' : contact.score >= 40 ? '#FFB800' : '#6B7280',
                }}
              >
                {contact.score}
              </span>
              <span className="score-max">/100</span>
            </div>
            <div className="score-track">
              <div
                className="score-track-fill"
                style={{
                  width: `${contact.score}%`,
                  background: contact.score >= 75
                    ? 'linear-gradient(90deg, #39FF14, #00FF88)'
                    : contact.score >= 40
                    ? 'linear-gradient(90deg, #FFB800, #FFD060)'
                    : 'linear-gradient(90deg, #6B7280, #9CA3AF)',
                }}
              />
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div className="detail-card" variants={cardVariants}>
            <h3 className="card-title">Contact Info</h3>
            <div className="info-list">
              {contact.email && (
                <div className="info-item">
                  <span className="info-label">Email</span>
                  <a href={`mailto:${contact.email}`} className="info-value info-link">{contact.email}</a>
                </div>
              )}
              {contact.phone && (
                <div className="info-item">
                  <span className="info-label">Phone</span>
                  <span className="info-value">{contact.phone}</span>
                </div>
              )}
              {contact.website && (
                <div className="info-item">
                  <span className="info-label">Website</span>
                  <a href={contact.website} target="_blank" rel="noreferrer" className="info-value info-link">{contact.website}</a>
                </div>
              )}
              {contact.location && (
                <div className="info-item">
                  <span className="info-label">Location</span>
                  <span className="info-value">{contact.location}</span>
                </div>
              )}
              {contact.tags?.length > 0 && (
                <div className="info-item info-item--tags">
                  <span className="info-label">Tags</span>
                  <div className="tags-wrap">
                    {contact.tags.map(tag => (
                      <span key={tag} className="tag">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Notes */}
          <motion.div className="detail-card detail-card--notes" variants={cardVariants}>
            <h3 className="card-title">Notes</h3>
            <textarea
              className="notes-textarea"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add notes about this contact..."
              rows={5}
            />
            <button
              className={`save-notes-btn ${notesSaved ? 'saved' : ''}`}
              onClick={handleSaveNotes}
              disabled={notesSaving}
            >
              {notesSaved ? 'Saved!' : notesSaving ? 'Saving...' : 'Save Notes'}
            </button>
          </motion.div>

          {/* Email Activity */}
          <motion.div className="detail-card" variants={cardVariants}>
            <h3 className="card-title">Email Activity</h3>
            <div className="activity-grid">
              {[
                { label: 'Emails Sent', value: contact.emailsSent || 0, color: '#FF1493' },
                { label: 'Opened', value: contact.emailsOpened || 0, color: '#A855F7' },
                { label: 'Clicked', value: contact.emailsClicked || 0, color: '#39FF14' },
              ].map(stat => (
                <div key={stat.label} className="activity-stat">
                  <span className="activity-value" style={{ color: stat.color }}>{stat.value}</span>
                  <span className="activity-label">{stat.label}</span>
                </div>
              ))}
            </div>
            <div className="activity-dates">
              {contact.lastEmailSentAt && (
                <div className="info-item">
                  <span className="info-label">Last Sent</span>
                  <span className="info-value">{new Date(contact.lastEmailSentAt).toLocaleDateString()}</span>
                </div>
              )}
              {contact.lastEmailOpenedAt && (
                <div className="info-item">
                  <span className="info-label">Last Opened</span>
                  <span className="info-value">{new Date(contact.lastEmailOpenedAt).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
